import React from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import AppliedFilterList from './AppliedFilterList'
import FilterGroup from './FilterGroup'
import { describeTree } from './summary'

const appliedFilterText = ({ label, clauses }) =>
  [
    label,
    clauses
      .map(({ verb, values, more }) => [verb, values.join(', '), more > 0 && `+${more} more`].filter(Boolean).join(' '))
      .join('; ')
  ].join(' ')

// Every filter the chart answers to, View and chart alike, one line each. A report chart's
// data-source filters live on the server, so they are only mentioned.
const summaryLines = ({ appliedFilters, viewGroupFilterCount, chartText }) => {
  const viewLines = appliedFilters ? appliedFilters.map(appliedFilterText) : ["Its data source's filters"]
  if (viewGroupFilterCount > 0) {
    viewLines.push(`The View's filter groups (${viewGroupFilterCount} filter${viewGroupFilterCount === 1 ? '' : 's'})`)
  }
  return [...viewLines.map((text) => ({ text, source: 'View' })), ...(chartText ? [{ text: chartText, source: 'Chart' }] : [])]
}

const FilterSummary = ({ lines }) => (
  <div className="bg-light rounded p-2 small" data-testid="chart-filter-total-summary">
    {lines.length ? (
      <>
        <div className="fw-bold mb-1">This chart shows transactions where:</div>
        <ul className="mb-0 ps-3">
          {lines.map(({ text, source }, index) => (
            <li key={`${source}-${index}`} data-testid="chart-filter-total-summary-line">
              {index > 0 && <span className="text-muted">and </span>}
              {text} <span className="text-muted">({source === 'View' ? 'from the View' : 'this chart only'})</span>
            </li>
          ))}
        </ul>
      </>
    ) : (
      <div className="text-muted">No filters. This chart shows everything in its View.</div>
    )}
  </div>
)

FilterSummary.propTypes = { lines: PropTypes.array.isRequired }

// The chart settings section, shown from its "Filter this chart" button: a summary of every
// filter the chart answers to, the View's filters it inherits, and its own AND/OR groups.
// `appliedFilters` lists the View's filters when they are known (a dashboard chart); a report
// chart's come from its data source on the server.
const ChartFilterGroups = ({
  draft,
  setDraft,
  filterColumns,
  appliedFilters = null,
  viewGroupFilterCount = 0,
  isSample = false,
  canEdit = true,
  dashboardId = null,
  showCaption = false,
  setShowCaption = null
}) => {
  if (isSample) {
    return (
      <div className="pt-3 small text-muted" data-testid="chart-filter-groups">
        A chart on a sample always shows the whole sample.
      </div>
    )
  }

  const columnFor = (leaf) => filterColumns.find(({ column }) => column.accessor_key === leaf.column)?.column
  const chartText = describeTree(draft, columnFor)

  return (
    <div className="pt-3" data-testid="chart-filter-groups">
      <FilterSummary lines={summaryLines({ appliedFilters, viewGroupFilterCount, chartText })} />

      {appliedFilters && (
        <>
          <hr />
          <div data-testid="filter-groups-applied">
            <div className="fw-bold small">From the View</div>
            <div className="text-muted small mb-2">Always applied. Change these on the View itself.</div>
            <AppliedFilterList appliedFilters={appliedFilters} emptyText="No column filters on the View." />
            {viewGroupFilterCount > 0 && (
              <div className="small text-muted mt-1">
                Plus the View&apos;s filter groups ({viewGroupFilterCount} filter{viewGroupFilterCount === 1 ? '' : 's'}).
              </div>
            )}
          </div>
        </>
      )}

      <hr />
      <div className="fw-bold small">This chart only</div>
      <div className="text-muted small mb-2">Narrows this chart further, on top of the View&apos;s filters.</div>
      {draft && (
        <FilterGroup
          group={draft}
          tree={draft}
          setTree={setDraft}
          isRoot
          canEdit={canEdit}
          filterColumns={filterColumns}
          dashboardId={dashboardId}
        />
      )}
      {setShowCaption && (
        <Form.Check
          type="checkbox"
          id="chart-filter-caption-toggle"
          className="mt-2 small"
          label="Show these filters under the chart, including in exports and print"
          checked={showCaption}
          disabled={!canEdit}
          onChange={(e) => setShowCaption(e.target.checked)}
          data-testid="chart-filter-caption-toggle"
        />
      )}
    </div>
  )
}

ChartFilterGroups.propTypes = {
  draft: PropTypes.object,
  setDraft: PropTypes.func.isRequired,
  filterColumns: PropTypes.array.isRequired,
  appliedFilters: PropTypes.array,
  viewGroupFilterCount: PropTypes.number,
  isSample: PropTypes.bool,
  canEdit: PropTypes.bool,
  dashboardId: PropTypes.string,
  showCaption: PropTypes.bool,
  setShowCaption: PropTypes.func
}

export default ChartFilterGroups
